import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, Users, MapPin, Mail, Phone, Award, UserCheck, UserX, Clock } from 'lucide-react';
import { registrationAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../contexts/ToastContext';

interface HackathonMember {
  _id: string;
  userId: string;
  fullName: string;
  email: string;
  phone: string;
  location: string;
  skills: string;
  experience: string;
  teamStatus: string;
  teamName: string | null;
  teamRole: string | null;
  registrationStatus: string;
  registrationDate: string;
  hackathonTitle: string;
}

const TeamMembersManagement: React.FC = () => {
  const [allMembers, setAllMembers] = useState<HackathonMember[]>([]);
  const [members, setMembers] = useState<HackathonMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [teamStatusFilter, setTeamStatusFilter] = useState<string>('all');
  const [hackathonFilter, setHackathonFilter] = useState<string>('all');
  const [exporting, setExporting] = useState(false);
  const [searching, setSearching] = useState(false);
  const { success, error } = useToast();

  useEffect(() => {
    fetchMembers();
  }, []); // Only fetch once on component mount

  useEffect(() => {
    // Client-side filtering
    let filtered = allMembers;

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(member => 
        member.fullName.toLowerCase().includes(searchLower) ||
        member.email.toLowerCase().includes(searchLower) ||
        member.phone.toLowerCase().includes(searchLower) ||
        member.location.toLowerCase().includes(searchLower) ||
        member.skills.toLowerCase().includes(searchLower) ||
        member.experience.toLowerCase().includes(searchLower)
      );
    }

    // Filter by registration status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(member => member.registrationStatus === statusFilter);
    }

    // Filter by team status
    if (teamStatusFilter !== 'all') {
      filtered = filtered.filter(member => member.teamStatus === teamStatusFilter);
    }

    // Filter by hackathon
    if (hackathonFilter !== 'all') {
      filtered = filtered.filter(member => member.hackathonTitle === hackathonFilter);
    }

    setMembers(filtered);
  }, [allMembers, searchTerm, statusFilter, teamStatusFilter, hackathonFilter]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await registrationAPI.getHackathonMembers({
        // Don't send search or status filters - fetch all data
      });
      setAllMembers(response.registrations || []);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to fetch members');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    try {
      setExporting(true);
      
      // Create CSV content
      const headers = [
        'Full Name',
        'Email',
        'Phone',
        'Location',
        'Skills',
        'Experience',
        'Hackathon',
        'Team Status',
        'Team Name',
        'Team Role',
        'Registration Status',
        'Registration Date',
        'Hackathon'
      ];
      
      const csvContent = [
        headers.join(','),
        ...members.map(member => [
          `"${member.fullName}"`,
          `"${member.email}"`,
          `"${member.phone || ''}"`,
          `"${member.location}"`,
          `"${member.skills}"`,
          `"${member.experience}"`,
          `"${member.hackathonTitle || 'Unknown Hackathon'}"`,
          `"${member.teamStatus}"`,
          `"${member.teamName || ''}"`,
          `"${member.teamRole || ''}"`,
          `"${member.registrationStatus}"`,
          `"${new Date(member.registrationDate).toLocaleDateString()}"`
        ].join(','))
      ].join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `hackathon-members-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      success('Members exported successfully!');
    } catch (err: any) {
      error('Failed to export members');
    } finally {
      setExporting(false);
    }
  };

  const getTeamStatusBadge = (status: string) => {
    switch (status) {
      case 'In Team':
        return <Badge variant="default" className="bg-green-100 text-green-800">In Team</Badge>;
      case 'Looking for Team':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Looking for Team</Badge>;
      case 'Pending Team Approval':
        return <Badge variant="secondary" className="bg-orange-100 text-orange-800">Pending Team Approval</Badge>;
      case 'Available':
        return <Badge variant="outline" className="bg-blue-100 text-blue-800">Available</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTeamRoleBadge = (role: string | null) => {
    if (!role) return null;
    
    switch (role) {
      case 'Leader':
        return <Badge variant="default" className="bg-purple-100 text-purple-800">Leader</Badge>;
      case 'Member':
        return <Badge variant="secondary" className="bg-gray-100 text-gray-800">Member</Badge>;
      default:
        return <Badge variant="outline">{role}</Badge>;
    }
  };

  const getRegistrationStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Badge variant="default" className="bg-green-100 text-green-800">Confirmed</Badge>;
      case 'registered':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Registered</Badge>;
      case 'attended':
        return <Badge variant="default" className="bg-blue-100 text-blue-800">Attended</Badge>;
      case 'withdrawn':
        return <Badge variant="destructive" className="bg-red-100 text-red-800">Withdrawn</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredMembers = members.filter(member => {
    if (teamStatusFilter !== 'all') {
      return member.teamStatus === teamStatusFilter;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Members Management</h1>
          <p className="text-gray-600">Manage and view all hackathon participants</p>
        </div>
        <Button
          onClick={exportToExcel}
          disabled={exporting}
          className="flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          {exporting ? 'Exporting...' : 'Export to Excel'}
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <div className="relative">
                <Input
                  placeholder="Search by name, email, phone, location, skills... (Client-side)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pr-8"
                />
                {searchTerm ? (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                ) : (
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                )}
              </div>
            </div>
            <div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Registration Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="registered">Registered</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="attended">Attended</SelectItem>
                  <SelectItem value="withdrawn">Withdrawn</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select value={teamStatusFilter} onValueChange={setTeamStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Team Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Team Status</SelectItem>
                  <SelectItem value="In Team">In Team</SelectItem>
                  <SelectItem value="Looking for Team">Looking for Team</SelectItem>
                  <SelectItem value="Pending Team Approval">Pending Team Approval</SelectItem>
                  <SelectItem value="Available">Available</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select value={hackathonFilter} onValueChange={setHackathonFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Hackathon" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Hackathons</SelectItem>
                  {Array.from(new Set(allMembers.map(member => member.hackathonTitle))).map(hackathon => (
                    <SelectItem key={hackathon} value={hackathon}>
                      {hackathon}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {allMembers.length > 0 && filteredMembers.length !== allMembers.length ? (
                  <span>Showing {filteredMembers.length} of {allMembers.length} members</span>
                ) : (
                  <span>Total Members: {filteredMembers.length}</span>
                )}
              </div>
              {(searchTerm || statusFilter !== 'all' || teamStatusFilter !== 'all' || hackathonFilter !== 'all') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setTeamStatusFilter('all');
                    setHackathonFilter('all');
                  }}
                  className="text-xs"
                >
                  Clear All Filters
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Members Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Hackathon Members ({filteredMembers.length})
            {allMembers.length > 0 && filteredMembers.length !== allMembers.length && (
              <span className="text-sm font-normal text-gray-500">
                (filtered from {allMembers.length})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-semibold">Name</th>
                  <th className="text-left py-3 px-4 font-semibold">Contact</th>
                  <th className="text-left py-3 px-4 font-semibold">Location</th>
                  <th className="text-left py-3 px-4 font-semibold">Skills</th>
                  <th className="text-left py-3 px-4 font-semibold">Experience</th>
                  <th className="text-left py-3 px-4 font-semibold">Hackathon</th>
                  <th className="text-left py-3 px-4 font-semibold">Team Status</th>
                  <th className="text-left py-3 px-4 font-semibold">Team Details</th>
                  <th className="text-left py-3 px-4 font-semibold">Registration</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((member) => (
                  <tr key={member._id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium">{member.fullName}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-sm">
                          <Mail className="h-3 w-3 text-gray-500" />
                          {member.email}
                        </div>
                        {member.phone && (
                          <div className="flex items-center gap-1 text-sm">
                            <Phone className="h-3 w-3 text-gray-500" />
                            {member.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-gray-500" />
                        <span className="text-sm">{member.location || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm max-w-xs truncate" title={member.skills}>
                        {member.skills || 'N/A'}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <Award className="h-3 w-3 text-gray-500" />
                        <span className="text-sm">{member.experience}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm font-medium text-blue-600">
                        {member.hackathonTitle || 'Unknown Hackathon'}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {getTeamStatusBadge(member.teamStatus)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="space-y-1">
                        {member.teamName && (
                          <div className="text-sm font-medium">{member.teamName}</div>
                        )}
                        {getTeamRoleBadge(member.teamRole)}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="space-y-1">
                        {getRegistrationStatusBadge(member.registrationStatus)}
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Clock className="h-3 w-3" />
                          {new Date(member.registrationDate).toLocaleDateString()}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredMembers.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No members found matching the current filters.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamMembersManagement; 